/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ChatUserRolesEnum } from 'constants/chats';
import { UserItemType } from 'types/callType';
import { AuthType } from 'types/stateTypes';
import { getInitialsFromFullname } from 'utils/user.utils';
import i18n from '../shared/i18n';
import {
  AuthorOfMessageType,
  ChatActionEnum,
  ChatDescription,
  ChatMessage,
  DialogType,
  SelectedChat,
  SelectedDialogUserInfo,
} from '../types/chatsType';
import { pluralize } from '../utils/text.utils';

export { getInitialsFromFullname };

export const getInterlocutorInitials = (chat: SelectedChat): string => {
  const { interlocutor, name } = chat;
  if (name) return getInitialsFromFullname(name);
  if (interlocutor) return getInitialsFromFullname(interlocutor.fullName);
  return '';
};

export const getInterlocutorName = (chat: SelectedChat): string => {
  const { interlocutor, name } = chat;
  if (name) return name;
  if (interlocutor) return interlocutor.fullName || interlocutor.nickname!;
  return '';
};

export const findGroupUserById = (
  users: SelectedDialogUserInfo[] | undefined,
  userId: string
): SelectedDialogUserInfo | undefined => {
  if (!userId) return;
  return users?.find((x) => x.userId === userId);
};

export const getInterlocutorImageSrc = (chat: SelectedChat): string => {
  const { interlocutor, image } = chat;
  if (image) return image.path;
  if (interlocutor) return interlocutor.image?.path ?? '';
  return '';
};

export function getUserListForUpdateStatus(chats: ChatDescription[]): string[] {
  const privateChats = chats.filter((chat) => chat.interlocutor);
  return privateChats.map((x) => x.interlocutor!.userId);
}

export const findChatById = (chats: ChatDescription[], chatId: number): ChatDescription | undefined =>
  chats.find((chat) => chat.id === chatId);

export const getAuthorOfMessage = (
  chat: SelectedChat,
  message: ChatMessage,
  user: AuthorOfMessageType,
  users?: SelectedDialogUserInfo[]
): AuthorOfMessageType => {
  if (!message.ownerId) {
    return {
      imgPath: chat.image?.path,
      name: chat.name,
    };
  }
  if (chat.type === DialogType.chat) {
    const authorId = message.ownerId;
    const interlocutorId = chat.interlocutor?.userId;

    if (authorId !== interlocutorId) return user;

    return {
      imgPath: chat.interlocutor?.image?.path,
      name: chat.interlocutor?.fullName,
    };
  }

  const groupUser = findGroupUserById(users, message.ownerId);
  return {
    imgPath: groupUser?.image?.path,
    name: groupUser?.fullName,
  };
};

export const isChatActionAvailable = (role: ChatActionEnum, chatType: DialogType, isUserAdmin: boolean): boolean => {
  const result = {
    delete: (chatType === DialogType.group && isUserAdmin) || chatType === DialogType.chat,
    edit: chatType === DialogType.group && isUserAdmin,
    leave: chatType === DialogType.group && !isUserAdmin,
  };
  return result[role];
};

export const getPluralizeCountUsers = (countUsers: number): string => {
  const w1 = i18n.t('page.chat.group.count.users.w1');
  const w2 = i18n.t('page.chat.group.count.users.w2');
  const w5 = i18n.t('page.chat.group.count.users.w3');
  return pluralize(countUsers, [w1, w2, w5], true);
};

export const isUserDeletable = (user: UserItemType, ownId: AuthType['id']): boolean =>
  user.role !== ChatUserRolesEnum.creator && user.userId !== ownId;
